import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import {
  ArrowUpRight,
  ShieldCheck,
  Mountain,
  Droplets,
  Layers,
  Award,
  Gauge,
  HardHat,
  Globe2,
  Drill,
  CheckCircle,
  Target,
  Zap,
  Users,
} from "lucide-react";
import heroImg from "@/assets/hero-drilling.jpg";
import aboutImg from "@/assets/about-drill.jpg";
import projSurface from "@/assets/project-surface.jpg";
import projUnderground from "@/assets/project-underground.jpg";
import projWater from "@/assets/project-water.jpg";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

// ─────────────────────────────────────────────
//  Shared animation variants & utilities
// ─────────────────────────────────────────────
const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.4, delayChildren: 0.2 },
  },
};

function Reveal({
  children,
  delay = 0,
  direction = "up",
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  direction?: "up" | "left" | "right" | "none";
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8%" });

  const hidden =
    direction === "up"
      ? { opacity: 0, y: 40 }
      : direction === "left"
        ? { opacity: 0, x: 60 }
        : direction === "right"
          ? { opacity: 0, x: -60 }
          : { opacity: 0 };

  const visible =
    direction === "up"
      ? { opacity: 1, y: 0 }
      : direction === "left"
        ? { opacity: 1, x: 0 }
        : direction === "right"
          ? { opacity: 1, x: 0 }
          : { opacity: 1 };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden,
        visible: { ...visible, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } },
      }}
    >
      {children}
    </motion.div>
  );
}

function StaggerGrid({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8%" });
  return (
    <motion.div
      ref={ref}
      className={className}
      variants={staggerContainer}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
    >
      {children}
    </motion.div>
  );
}

function StaggerItem({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 35 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
      }}
    >
      {children}
    </motion.div>
  );
}

export default function Index() {
  return (
    <>
      <Helmet>
        <title>Salmamy Drill — Expert en Forage Minier et Hydraulique Afrique de l'Ouest</title>
        <meta name="description" content="Salmamy Drill opère des forages premium (Surface, Souterrain, Eau) en Afrique de l'Ouest. Équipements Boart Longyear, précision absolue, sécurité HSE. Devis sous 24h." />
        <meta name="keywords" content="forage minier, forage surface, forage souterrain, forage eau, Afrique de l'Ouest, Mali, Boart Longyear, exploration minière, hydrologie, géotechnique" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Salmamy Drill — Expert en Forage Minier et Hydraulique" />
        <meta property="og:description" content="Expert en forage de surface, souterrain et hydraulique en Afrique de l'Ouest. Équipements Boart Longyear, précision absolue." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://salmamydrill.com" />
        <meta property="og:image" content={heroImg} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Salmamy Drill — Expert en Forage Minier" />
        <meta name="twitter:description" content="Forage de surface, souterrain et hydraulique en Afrique de l'Ouest. Équipements Boart Longyear." />
        <meta name="twitter:image" content={heroImg} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Inter:wght@400;500;600&display=swap" />
      </Helmet>

      <div className="min-h-screen bg-background text-foreground flex flex-col overflow-x-hidden">
        <Nav />
        <Hero />
        <Marquee />
        <About />
        <Atouts />
        <Services />
        <Projects />
        <Reassurance />
        <CTA />
        <Footer />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
//  HERO
// ─────────────────────────────────────────────
function Hero() {
  const { t } = useTranslation("home");
  const stats = t("hero.stats", { returnObjects: true }) as { k: string; v: string; u: string }[];
  return (
    <section className="relative bg-surface text-surface-foreground pt-32 pb-24 md:pt-48 md:pb-32 px-6 grain overflow-hidden min-h-[95vh] flex items-center">
      <div className="absolute inset-0 bg-gradient-to-b from-surface via-surface/95 to-background/5" />

      {/* Background photo — animated scale-in */}
      <motion.div
        initial={{ opacity: 0, scale: 1.08 }}
        animate={{ opacity: 0.3, scale: 1 }}
        transition={{ duration: 2.5, ease: "easeOut" }}
        className="absolute right-0 top-0 w-full md:w-2/3 h-full pointer-events-none mix-blend-screen"
      >
        <img src={heroImg} alt="Plateforme de forage" className="w-full h-full object-cover mask-image-l grayscale contrast-125" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto w-full flex flex-col items-start pt-10">

        {/* Eyebrow */}
        <motion.span
          className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-10"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.span
            className="h-px bg-primary/40 block"
            initial={{ width: 0 }}
            animate={{ width: 48 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          />
          {t("hero.eyebrow")}
        </motion.span>

        {/* H1 — line by line */}
        <div className="overflow-hidden mb-3">
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[7.5rem] font-display font-black leading-[0.85] tracking-tighter uppercase text-surface-foreground">
              {t("hero.line1_start")} <span className="text-primary italic">{t("hero.line1_end")}</span>
            </h1>
          </motion.div>
        </div>
        <div className="overflow-hidden mb-10">
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[7.5rem] font-display font-black leading-[0.85] tracking-tighter uppercase text-surface-foreground">
              {t("hero.line2_start")} <span className="text-primary italic">{t("hero.line2_end")}</span>
            </h1>
          </motion.div>
        </div>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-surface-foreground/70 max-w-2xl leading-relaxed border-l-2 border-primary pl-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          dangerouslySetInnerHTML={{ __html: t("hero.subtitle") }}
        />

        {/* CTA buttons */}
        <motion.div
          className="flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <a href="/contact" className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-sm font-bold tracking-widest uppercase hover:bg-primary/90 transition shadow-2xl">
            {t("hero.cta_primary")}
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
          <a href="#expertise" className="inline-flex items-center gap-3 border border-surface-foreground/20 text-surface-foreground px-8 py-5 text-sm font-bold tracking-widest uppercase hover:bg-surface-foreground/5 transition">
            {t("hero.cta_secondary")}
          </a>
        </motion.div>

        {/* Stats row */}
        <StaggerGrid className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-12 border-t border-surface-foreground/10 pt-12 w-full max-w-4xl">
          {stats.map((s) => (
            <StaggerItem key={s.k}>
              <div className="text-[0.65rem] uppercase tracking-widest text-surface-foreground/50 font-semibold mb-2">{s.k}</div>
              <div className="font-display text-4xl font-black text-surface-foreground">
                {s.v}<span className="text-primary text-xl ml-1">{s.u}</span>
              </div>
            </StaggerItem>
          ))}
        </StaggerGrid>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  MARQUEE
// ─────────────────────────────────────────────
function Marquee() {
  const { t } = useTranslation("home");
  const items = t("marquee", { returnObjects: true }) as string[];
  return (
    <div className="bg-primary text-primary-foreground py-4 overflow-hidden shadow-2xl relative z-20">
      <div className="flex gap-16 whitespace-nowrap animate-[scroll_40s_linear_infinite] text-xs font-bold tracking-[0.4em] uppercase">
        {[...items, ...items, ...items].map((i, idx) => (
          <span key={idx} className="flex items-center gap-16">{i} <span className="text-primary-foreground/40 font-serif">/</span></span>
        ))}
      </div>
      <style>{`@keyframes scroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
//  ABOUT
// ─────────────────────────────────────────────
function About() {
  const { t } = useTranslation("home");
  const strengths = [
    { i: ShieldCheck, label: t("about.strengths.safety.t"), desc: t("about.strengths.safety.d") },
    { i: Award, label: t("about.strengths.certs.t"), desc: t("about.strengths.certs.d") },
    { i: Gauge, label: t("about.strengths.precision.t"), desc: t("about.strengths.precision.d") },
    { i: HardHat, label: t("about.strengths.crew.t"), desc: t("about.strengths.crew.d") },
  ];
  return (
    <section id="expertise" className="relative py-20 md:py-32 bg-background text-foreground overflow-hidden border-t border-border">
      <div className="mx-auto max-w-7xl px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">

          {/* Left – image + floating card */}
          <div className="relative">
            <Reveal direction="left">
              <div className="relative w-full aspect-[4/5] overflow-hidden shadow-2xl">
                <img src={aboutImg} alt="Machinerie de forage haut de gamme" className="absolute inset-0 w-full h-full object-cover contrast-125 hover:grayscale-0 transition-all duration-1000 opacity-90" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-background/40 to-transparent pointer-events-none" />
              </div>
            </Reveal>

            <Reveal direction="up" delay={0.3} className="absolute -bottom-10 left-4 right-4 w-auto md:left-auto md:-right-12 md:w-[420px]">
              <div className="bg-white/90 backdrop-blur-xl border border-border p-8 md:p-10 shadow-2xl group">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-6 relative">
                  <Globe2 className="w-6 h-6 text-primary relative z-10" strokeWidth={2} />
                </div>
                <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-foreground transition-colors group-hover:text-primary">
                  {t("about.card_title1")} <br />
                  <span className="text-primary italic">{t("about.card_title2")}</span>
                </h3>
                <p className="text-sm text-foreground/70 leading-relaxed font-medium mb-6">
                  {t("about.card_body")}
                </p>
                <a href="/equipements" className="inline-flex items-center gap-2 border border-border text-[0.65rem] uppercase font-bold tracking-[0.2em] px-4 py-2 hover:bg-primary hover:border-primary hover:text-primary-foreground transition-all">
                  {t("about.card_cta")} <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right – text */}
          <div className="pt-16 lg:pt-0 pb-12">
            <Reveal direction="up" delay={0.1}>
              <span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-8">
                <span className="w-12 h-px bg-primary/40 block"></span>
                {t("about.eyebrow")}
              </span>
            </Reveal>
            <Reveal direction="up" delay={0.2}>
              <h2 className="text-4xl md:text-5xl lg:text-7xl font-display font-black leading-[0.9] tracking-tighter uppercase text-foreground mb-8">
                {t("about.title1")} <br />{t("about.title2")} <span className="text-foreground/30 italic">{t("about.title2_accent")}</span>
              </h2>
            </Reveal>
            <Reveal direction="up" delay={0.35}>
              <div className="pl-6 border-l-2 border-primary mb-12">
                <p className="text-xl md:text-2xl font-medium text-foreground/80 leading-relaxed mb-6">{t("about.p1")}</p>
                <p className="text-lg text-foreground/60 leading-relaxed font-medium">{t("about.p2")}</p>
              </div>
            </Reveal>

            <StaggerGrid className="grid sm:grid-cols-2 gap-x-8 gap-y-12">
              {strengths.map((b) => (
                <StaggerItem key={b.label}>
                  <div className="group border-t border-border pt-6">
                    <b.i className="w-8 h-8 text-primary mb-4 opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all" strokeWidth={1} />
                    <h4 className="font-display font-bold text-lg mb-2 uppercase tracking-wide text-foreground">{b.label}</h4>
                    <p className="text-sm text-foreground/50 font-medium leading-relaxed">{b.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGrid>
          </div>

        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  ATOUTS
// ─────────────────────────────────────────────
function Atouts() {
  const { t } = useTranslation("home");
  const items = t("atouts.items", { returnObjects: true }) as { t: string; d: string }[];
  const icons = [CheckCircle, Target, Zap, Users];
  const numbers = ["01", "02", "03", "04"];
  return (
    <section className="py-20 md:py-32 bg-surface text-surface-foreground grain relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-surface via-surface/95 to-background/5" />
      <div className="mx-auto max-w-7xl px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal direction="up">
              <span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-8">
                <span className="w-12 h-px bg-primary/40 block"></span>
                {t("atouts.eyebrow")}
              </span>
            </Reveal>
            <Reveal direction="up" delay={0.1}>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-black leading-[0.9] uppercase tracking-tighter mb-8">
                {t("atouts.title")} <span className="text-primary italic">{t("atouts.title_accent")}</span>
              </h2>
            </Reveal>
            <Reveal direction="up" delay={0.2}>
              <p className="text-xl text-surface-foreground/70 max-w-xl font-medium leading-relaxed mb-12 pl-6 border-l-2 border-primary">
                {t("atouts.body")}
              </p>
            </Reveal>
            <Reveal direction="up" delay={0.3}>
              <a href="/contact" className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-primary/90 transition shadow-xl">
                {t("atouts.cta")} <ArrowUpRight className="w-4 h-4" />
              </a>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {items.map((a, idx) => {
              const Icon = icons[idx];
              return (
                <Reveal key={idx} direction="up" delay={idx * 0.1}>
                  <div className="group relative bg-background/50 backdrop-blur-sm border border-surface-foreground/10 p-8 hover:bg-background hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                    <div className="absolute top-4 right-4 font-display text-6xl font-black text-surface-foreground/5 group-hover:text-primary/10 transition-colors">
                      {numbers[idx]}
                    </div>
                    <div className="relative z-10">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-500">
                        <Icon className="w-8 h-8 text-primary group-hover:text-primary-foreground" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-display text-2xl font-black uppercase tracking-wide mb-3 text-surface-foreground group-hover:text-primary transition-colors">
                        {a.t}
                      </h3>
                      <p className="text-sm text-surface-foreground/60 leading-relaxed font-medium group-hover:text-foreground/80 transition-colors">
                        {a.d}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  SERVICES
// ─────────────────────────────────────────────
function Services() {
  const { t } = useTranslation("home");
  const icons = [Mountain, Layers, Droplets];
  const urls = ["/forage-surface", "/forage-souterrain", "/forage-eau"];
  const serviceKeys = ["surface", "underground", "water"] as const;
  return (
    <section id="services" className="py-20 md:py-32 bg-surface text-surface-foreground grain">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
          <Reveal direction="up" className="max-w-2xl">
            <span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase">
              <span className="w-12 h-px bg-primary/40 block"></span>
              {t("services.eyebrow")}
            </span>
            <h2 className="mt-6 text-4xl md:text-5xl font-display font-black leading-[0.9] uppercase">{t("services.title")} <br /><span className="text-surface-foreground/40 italic">{t("services.title_accent")}</span></h2>
          </Reveal>
          <Reveal direction="up" delay={0.2}>
            <a href="/contact" className="inline-flex items-center gap-3 bg-surface-foreground/5 hover:bg-primary hover:text-primary-foreground px-8 py-4 text-sm font-bold tracking-widest uppercase transition-colors">
              {t("services.cta")} <ArrowUpRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>

        <StaggerGrid className="grid md:grid-cols-3 gap-8">
          {serviceKeys.map((key, idx) => {
            const Icon = icons[idx];
            const svc = t(`services.${key}`, { returnObjects: true }) as { t: string; d: string; k: string[] };
            return (
              <StaggerItem key={key}>
                <a href={urls[idx]} className="group relative bg-surface-foreground/5 border border-surface-foreground/10 p-10 hover:bg-surface-foreground/10 transition-colors flex flex-col justify-between min-h-[450px] block">
                  <div>
                    <div className="flex items-center justify-between mb-12">
                      <Icon className="w-12 h-12 text-primary group-hover:scale-110 transition-transform duration-500" strokeWidth={1} />
                      <span className="font-display text-4xl font-black text-surface-foreground/10 group-hover:text-primary/20 transition-colors">0{idx + 1}</span>
                    </div>
                    <h3 className="text-3xl font-display font-black uppercase leading-none mb-6">{svc.t}</h3>
                    <p className="text-surface-foreground/70 leading-relaxed font-medium mb-8">{svc.d}</p>
                  </div>
                  <div>
                    <div className="flex flex-wrap gap-2 mb-8">
                      {svc.k.map((k: string) => (
                        <span key={k} className="text-[0.65rem] uppercase tracking-widest font-bold px-3 py-1.5 border border-surface-foreground/10 text-surface-foreground/50 group-hover:border-primary/50 group-hover:text-primary transition-colors">{k}</span>
                      ))}
                    </div>
                    <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-primary">
                      {t("services.explore")} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>
              </StaggerItem>
            );
          })}
        </StaggerGrid>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  PROJECTS
// ─────────────────────────────────────────────
function Projects() {
  const { t } = useTranslation("home");
  const projects = [
    { img: projSurface, cat: t("projects.p1.cat"), title: t("projects.p1.t"), desc: t("projects.p1.d") },
    { img: projUnderground, cat: t("projects.p2.cat"), title: t("projects.p2.t"), desc: t("projects.p2.d") },
    { img: projWater, cat: t("projects.p3.cat"), title: t("projects.p3.t"), desc: t("projects.p3.d") },
  ];
  return (
    <section id="projets" className="py-20 md:py-32 px-6">
      <div className="mx-auto max-w-7xl">
        <div className="grid md:grid-cols-[1fr_auto] items-end mb-16 gap-8">
          <Reveal direction="up">
            <span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-6">
              <span className="w-12 h-px bg-primary/40 block"></span>
              {t("projects.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-5xl font-display font-black uppercase leading-[0.9]">{t("projects.title")} <span className="text-foreground/30 italic">{t("projects.title_accent")}</span></h2>
          </Reveal>
          <Reveal direction="up" delay={0.2}>
            <p className="text-xl text-foreground/60 max-w-sm font-medium leading-relaxed">
              {t("projects.subtitle")}
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-12 gap-8">
          <Reveal direction="up" delay={0.1} className="md:col-span-12 lg:col-span-8">
            <ProjectCard project={projects[0]} tall />
          </Reveal>
          <div className="md:col-span-12 lg:col-span-4 flex flex-col gap-8">
            <Reveal direction="up" delay={0.25}>
              <ProjectCard project={projects[1]} />
            </Reveal>
            <Reveal direction="up" delay={0.4}>
              <ProjectCard project={projects[2]} />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ project, className = "", tall = false }: { project: { img: string; cat: string; title: string; desc: string }; className?: string; tall?: boolean }) {
  return (
    <div className={`group relative overflow-hidden bg-surface block border border-border shadow-2xl ${className} ${tall ? "h-[600px] lg:h-[700px]" : "h-[400px] lg:h-[334px]"}`}>
      <img src={project.img} alt={project.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 mix-blend-luminosity group-hover:scale-105 group-hover:grayscale-0 transition-all duration-[1500ms]" />
      <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/80 to-transparent opacity-90 group-hover:opacity-70 transition-opacity duration-1000" />
      <div className="absolute top-6 left-6">
        <span className="bg-primary text-primary-foreground px-4 py-1.5 text-[0.6rem] font-bold tracking-[0.2em] uppercase shadow-lg">{project.cat}</span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-8 text-surface-foreground transition-transform duration-500 group-hover:-translate-y-2">
        <h3 className="font-display text-3xl font-black uppercase leading-[0.9] mb-4">{project.title}</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-surface-foreground/70 uppercase tracking-widest">{project.desc}</span>
          <div className="w-10 h-10 rounded-full bg-surface-foreground/10 flex items-center justify-center backdrop-blur-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
            <ArrowUpRight className="w-5 h-5" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  REASSURANCE
// ─────────────────────────────────────────────
function Reassurance() {
  const { t } = useTranslation("home");
  const stats = t("stats", { returnObjects: true }) as { v: string; k: string }[];
  return (
    <section className="py-24 bg-surface text-surface-foreground px-6 grain">
      <div className="mx-auto max-w-7xl">
        <StaggerGrid className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-16 border-t border-surface-foreground/10 pt-16">
          {stats.map((s) => (
            <StaggerItem key={s.k}>
              <div className="font-display text-5xl md:text-7xl font-black text-primary leading-[0.8] tracking-tighter mb-4">{s.v}</div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-surface-foreground/50 border-l border-primary pl-4 py-1">{s.k}</div>
            </StaggerItem>
          ))}
        </StaggerGrid>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  CTA
// ─────────────────────────────────────────────
function CTA() {
  const { t } = useTranslation("home");
  const ctaStats = t("cta_section.stats", { returnObjects: true }) as { v: string; l: string }[];
  return (
    <section className="bg-background text-foreground py-20 md:py-32 px-6 grain relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-surface/5" />
      <div className="mx-auto max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-16 items-center">
          <div>
            <Reveal direction="up">
              <span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-8">
                <span className="w-12 h-px bg-primary/40 block"></span>
                {t("cta_section.eyebrow")}
              </span>
            </Reveal>
            <Reveal direction="up" delay={0.1}>
              <h2 className="text-4xl md:text-5xl lg:text-7xl font-display font-black leading-[0.85] uppercase tracking-tighter mb-6">
                {t("cta_section.title")} <span className="text-primary italic">{t("cta_section.title_accent")}</span> {t("cta_section.title_end")}
              </h2>
            </Reveal>
            <Reveal direction="up" delay={0.2}>
              <p className="text-xl md:text-2xl text-foreground/70 max-w-xl font-medium leading-relaxed mb-10">
                {t("cta_section.body")}
              </p>
            </Reveal>

            <Reveal direction="up" delay={0.3}>
              <div className="grid grid-cols-3 gap-6 mb-12">
                {ctaStats.map((s, idx) => (
                  <div key={idx} className="text-center lg:text-left">
                    <div className="font-display text-3xl md:text-4xl font-black text-primary leading-[0.9]">{s.v}</div>
                    <div className="text-[0.65rem] uppercase tracking-widest font-bold text-foreground/50 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal direction="up" delay={0.4}>
              <motion.a
                href="/contact"
                className="group inline-flex items-center gap-4 bg-primary text-primary-foreground px-10 py-5 text-sm font-bold tracking-widest uppercase shadow-xl hover:shadow-2xl transition-all"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
              >
                {t("cta_section.cta")}
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </motion.a>
            </Reveal>
          </div>

          <Reveal direction="up" delay={0.5}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl blur-2xl opacity-50" />
              <div className="relative bg-surface backdrop-blur-md border border-foreground/20 p-10 md:p-12 shadow-2xl rounded-2xl">
                <div className="space-y-8">
                  <div className="flex items-start gap-4 group">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-2xl flex-shrink-0 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                      <Drill className="w-7 h-7 text-primary group-hover:text-primary-foreground" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-black uppercase tracking-wide mb-2 text-surface-foreground group-hover:text-primary transition-colors">{t("cta_section.expertise_title")}</h3>
                      <p className="text-sm text-surface-foreground/80 leading-relaxed">{t("cta_section.expertise_body")}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-2xl flex-shrink-0 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                      <Zap className="w-7 h-7 text-primary group-hover:text-primary-foreground" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-black uppercase tracking-wide mb-2 text-surface-foreground group-hover:text-primary transition-colors">{t("cta_section.response_title")}</h3>
                      <p className="text-sm text-surface-foreground/80 leading-relaxed">{t("cta_section.response_body")}</p>
                    </div>
                  </div>

                  <div className="pt-8 border-t border-foreground/20 space-y-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                        <span className="text-xs font-bold uppercase tracking-widest text-surface-foreground/70">{t("cta_section.email_label")}</span>
                      </div>
                      <a href="mailto:contact@salmamydrill.com" className="text-lg font-display font-black text-surface-foreground hover:text-primary transition-colors block">
                        contact@salmamydrill.com
                      </a>
                    </div>

                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                        <span className="text-xs font-bold uppercase tracking-widest text-surface-foreground/70">{t("cta_section.phone_label")}</span>
                      </div>
                      <a href="tel:+22320217502" className="text-lg font-display font-black text-surface-foreground hover:text-primary transition-colors block">
                        +223 20 21 75 02
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
